import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { useServices } from "@/lib/useServices";

export const useFresha = () => {
  const { data } = useQuery({ queryKey: ["fresha"], queryFn: () => api.get("/fresha").then((r) => r.data), staleTime: 300_000 });
  return data?.enabled ? data : null;
};

export const useFreshaFor = () => {
  const fresha = useFresha();
  const { data } = useServices();
  if (!fresha) return null;
  return (key) => data?.categories.flatMap((c) => c.items).find((s) => s.key === key)?.fresha_url || fresha.url;
};

const W = 500;
const H = 860;
const desktop = () => window.matchMedia("(min-width: 900px) and (pointer: fine)").matches;
const isFresha = (url) => /^https:\/\/(www\.)?fresha\.com\//.test(url || "");
let win = null;

const popup = (url) => {
  const h = Math.min(H, window.screen.availHeight - 60);
  const left = Math.round(window.screenX + (window.outerWidth - W) / 2);
  const top = Math.round(window.screenY + Math.max(0, (window.outerHeight - h) / 2));
  if (win && !win.closed) { win.location.href = url; win.focus(); return true; }
  win = window.open(url, "wm_fresha", `popup=yes,width=${W},height=${h},left=${left},top=${top}`);
  if (!win) return false;
  try { win.opener = null; } catch { }
  const opened = win;
  const timer = setInterval(() => {
    if (!opened.closed) return;
    clearInterval(timer);
    toast.success("Welcome back to Wax Moi", { description: "If you completed your booking, Fresha will email your confirmation.", id: "fresha-back" });
  }, 700);
  return true;
};

export const openFresha = (url) => {
  if (desktop() && popup(url)) return;
  window.open(url, "_blank", "noopener,noreferrer");
};

export const FreshaPopupLinks = () => {
  useEffect(() => {
    const onClick = (e) => {
      const a = e.target.closest?.("a[href]");
      if (!a || !isFresha(a.href) || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || !desktop()) return;
      if (popup(a.href)) e.preventDefault();
    };
    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
  }, []);
  return null;
};

export const BookLink = ({ className, testId, children, onClick, internal = "#book", as: As = "a" }) => {
  const fresha = useFresha();
  if (fresha) {
    return <a className={className} href={fresha.url} target="_blank" rel="noopener noreferrer" data-testid={testId} data-fresha="true">{children}</a>;
  }
  return As === "a" ? <a className={className} href={internal} onClick={onClick} data-testid={testId}>{children}</a> : <As className={className} to={internal} onClick={onClick} data-testid={testId}>{children}</As>;
};

export const FreshaBookPanel = ({ url }) => (
  <div className="form fresha-panel" data-testid="fresha-book-panel">
    <h3>Book online with Fresha</h3>
    <p>Choose your treatment, team member and time on our Fresha page. You'll get an instant confirmation from Fresha.</p>
    <a className="btn gold" href={url} target="_blank" rel="noopener noreferrer" data-testid="fresha-book-button">Book on Fresha <ArrowUpRight size={14} /></a>
  </div>
);
